// Rendu d'un formulaire de démarche à partir de son schéma, avec le moteur partagé
// (packages/contrats/src/demarches-moteur-formulaire.ts) : sections et champs visibles selon les
// conditions, valeurs par défaut, calculs affichés, erreurs de validation par champ. Même code pour
// l'aperçu de l'éditeur, le guichet et la lecture d'un dossier.
import {
  evaluerCalcul,
  validerDonnees,
  valeursParDefaut,
  visibilites,
  type DonneesFormulaire,
  type Formulaire,
  type ResultatValidation,
} from '@cc/contrats';
import { useMemo } from 'react';
import { ChampDynamique, type PieceDisponible } from './ChampDynamique';
import styles from './formulaire.module.css';

export interface ProprietesRenduFormulaire {
  formulaire: Formulaire;
  valeurs: DonneesFormulaire;
  onChanger: (valeurs: DonneesFormulaire) => void;
  /** Erreurs à afficher (après une tentative d'envoi) ; sans elles, aucune erreur n'est montrée. */
  erreurs?: { champ: string; message: string }[];
  pieces?: PieceDisponible[];
  tarifs?: Record<string, number>;
  disabled?: boolean;
  /** Titre de niveau des sections (h2 par défaut). */
  niveau?: 2 | 3;
}

/** Valeurs de départ : valeurs par défaut du schéma complétées des valeurs connues. */
export function valeursInitiales(f: Formulaire, connues: DonneesFormulaire = {}): DonneesFormulaire {
  return { ...valeursParDefaut(f), ...connues };
}

/** Validation complète (non partielle) des valeurs saisies, avec les mêmes règles que le serveur. */
export function validerSaisie(f: Formulaire, valeurs: DonneesFormulaire, options: { pieces?: PieceDisponible[]; tarifs?: Record<string, number>; partiel?: boolean } = {}): ResultatValidation {
  return validerDonnees(f, valeurs, { partiel: options.partiel, tarifs: options.tarifs, pieces: options.pieces ? new Set(options.pieces.map((p) => p.id)) : undefined });
}

export function RenduFormulaire({ formulaire, valeurs, onChanger, erreurs = [], pieces, tarifs = {}, disabled, niveau = 2 }: ProprietesRenduFormulaire) {
  const vis = useMemo(() => visibilites(formulaire, valeurs), [formulaire, valeurs]);
  const Titre = niveau === 2 ? 'h2' : 'h3';
  const erreurDe = (code: string) => erreurs.find((e) => e.champ === code || e.champ.startsWith(`${code}.`))?.message;
  const sections = formulaire.sections.filter((s) => vis.sections.has(s.code) || (!s.visible_si && s.champs.length === 0));
  if (sections.length === 0) {
    return <p className={styles.vide}>Ce formulaire ne contient aucun champ visible.</p>;
  }
  return (
    <div className={styles.formulaire}>
      {sections.map((s) => (
        <section key={s.code} className={styles.section} aria-labelledby={`section-${s.code}`}>
          <Titre id={`section-${s.code}`}>{s.titre}</Titre>
          {s.description ? <p className={styles.description}>{s.description}</p> : null}
          <div className={styles.champs}>
            {s.champs
              .filter((c) => vis.champs.has(c.code))
              .map((c) => (
                <ChampDynamique
                  key={c.code}
                  champ={c}
                  valeur={valeurs[c.code]}
                  onChanger={(v) => {
                    const suivant = { ...valeurs };
                    if (v === undefined || v === '' || v === null) delete suivant[c.code];
                    else suivant[c.code] = v;
                    onChanger(suivant);
                  }}
                  erreur={erreurDe(c.code)}
                  erreurs={erreurs}
                  pieces={pieces}
                  disabled={disabled}
                  valeurCalculee={c.calcul ? evaluerCalcul(c.calcul, valeurs, tarifs) : undefined}
                />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}
